import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, gr } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Account() {
  const qc = useQueryClient();
  const [addN, setAddN] = useState("");
  const { data: sub, isLoading } = useQuery({ queryKey: ["subscription"], queryFn: async () => (await api.get("/subscription")).data });

  const renew = async () => {
    try {
      await api.post("/subscription/renew");
      await qc.invalidateQueries({ queryKey: ["subscription"] });
      toast.success("Η συνδρομή ανανεώθηκε");
    } catch (e) { toast.error(e.response?.data?.detail || "Σφάλμα"); }
  };

  const addSlots = async () => {
    const n = Number(addN);
    if (!n || n < 1) return toast.error("Δώστε αριθμό θέσεων");
    try {
      const res = (await api.post("/subscription/add-slots", { additional: n })).data;
      await qc.invalidateQueries({ queryKey: ["subscription"] });
      toast.success(`Προστέθηκαν ${n} θέσεις (επιπλέον ${res.extra_cost}€)`);
      setAddN("");
    } catch (e) { toast.error(e.response?.data?.detail || "Σφάλμα"); }
  };

  if (isLoading || !sub) return <div className="p-6 text-muted-foreground">Φόρτωση...</div>;

  const daysLeft = sub.active_until ? Math.max(0, Math.ceil((new Date(sub.active_until) - new Date()) / 86400000)) : 0;
  const proratedCost = Number(addN || 0) * 20 * (daysLeft / 365);

  return (
    <div>
      <PageHeader title="Διαχείριση λογαριασμού NAVIPARK" subtitle="Συνδρομή & θέσεις parking" />
      <div className="max-w-2xl space-y-4 p-6">
        <Card className="rounded-sm border-border p-5 shadow-none" data-testid="account-status">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Κατάσταση συνδρομής</span>
            <span className={`rounded-sm px-2 py-0.5 text-xs font-bold ${sub.active ? "bg-[#DCFCE7] text-[#065F46]" : "bg-[#FEE2E2] text-[#991B1B]"}`} data-testid="account-active">
              {sub.active ? "Ενεργή" : "Ανενεργή"}
            </span>
          </div>
          <div className="mt-4 grid grid-cols-3 gap-3 text-center">
            <div><div className="text-[11px] text-muted-foreground">Θέσεις</div><div className="font-head text-xl font-bold" data-testid="account-slots">{sub.slots}</div></div>
            <div><div className="text-[11px] text-muted-foreground">Ενεργή έως</div><div className="font-semibold" data-testid="account-until">{gr((sub.active_until || "").slice(0, 10))}</div></div>
            <div><div className="text-[11px] text-muted-foreground">Ετήσιο σύνολο</div><div className="font-head text-xl font-bold text-[#0044CC]" data-testid="account-total">{sub.annual_total.toLocaleString("el-GR")} €</div></div>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">Χρέωση 20€ ανά θέση / έτος.</p>
          <Button onClick={renew} className="mt-4 rounded-sm bg-[#0055FF] hover:bg-[#0044CC]" data-testid="account-renew">Ανανέωση συνδρομής (+1 έτος)</Button>
        </Card>

        <Card className="rounded-sm border-border p-5 shadow-none">
          <h3 className="font-head font-bold">Προσθήκη θέσεων</h3>
          <p className="mt-1 text-xs text-muted-foreground">
            Η χρέωση είναι <b>αναλογική</b> (20€/θέση/έτος) μόνο για το διάστημα μέχρι την ανανέωση της συνδρομής ({gr((sub.active_until || "").slice(0, 10))}). Απομένουν {daysLeft} ημέρες.
          </p>
          <div className="mt-3 flex items-end gap-3">
            <div className="w-40">
              <Label className="text-xs text-muted-foreground">Επιπλέον θέσεις</Label>
              <Input type="number" min="1" value={addN} onChange={(e) => setAddN(e.target.value)} className="mt-1 rounded-sm" data-testid="account-add-input" />
            </div>
            <div className="pb-2 text-sm text-muted-foreground" data-testid="account-add-cost">Κόστος (αναλογικά): <b>{proratedCost.toLocaleString("el-GR", { maximumFractionDigits: 2 })} €</b></div>
            <Button onClick={addSlots} className="rounded-sm bg-[#0055FF] hover:bg-[#0044CC]" data-testid="account-add-btn">Προσθήκη</Button>
          </div>
        </Card>

        <p className="text-center text-[11px] text-muted-foreground">Οι πληρωμές (Stripe) θα ενεργοποιηθούν μετά τη φάση δοκιμών.</p>
      </div>
    </div>
  );
}
